import { useDeckBinding } from './DeckDataContext';
// DeckRefColumn.jsx
//
// Model / agent / tool deck columns: pick a resource; model/agent show a start
// panel until the user sends a first message, then promote to a chat column.
// Tool embeds a compact runner.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
  Keyboard,
} from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore, useAgentStore, useToastStore, useToolStore } from '@/store';
import { agentsApi, toolsApi, modelsApi, toolRunsApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolForm from '@/components/Tools/ToolForm';
import ToolResultDisplay from '@/components/Tools/ToolResultDisplay';
import ToolProgressIndicator from '@/components/Tools/ToolProgressIndicator';
import ToolEmptyResult from '@/components/Tools/ToolEmptyResult';
import {
  OUTPUT_TYPE_ICON,
  getRunStatusVisual,
} from '@/components/Tools/toolDetailHelpers';
import { deckPaneStyles } from './deckPaneStyles';
import DeckResourceStartPanel from '@/components/Deck/DeckResourceStartPanel';
import DeckColumnHeader from '@/components/Deck/DeckColumnHeader';

const TYPE_META = {
  model: { label: 'Choose a model' },
  agent: { label: 'Choose a bot' },
  tool: { label: 'Choose a tool' },
};

const EMPTY_TOOL_RUNS = [];

/**
 * Tiny pill rendered above the tool form (and inside DeckResourceStartPanel)
 * when a column was cloned from a DeckTemplate. Keeps the recipe context
 * visible after the user jumps from /decks/recipes/<slug> into the deck.
 */
function RecipeHintBanner({ hint, colors }) {
  if (!hint || (!hint.label && !hint.subtitle)) return null;
  return (
    <View
      style={[
        styles.recipeHintBanner,
        { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
      ]}
    >
      <View style={styles.recipeHintBannerHeader}>
        <Icon name="table-columns" size={10} color={colors.text_color_tertiary} solid />
        <Text size="xxs" weight="bold" color="tertiary" style={styles.recipeHintBannerEyebrow}>
          {`FROM RECIPE${hint.stepIndex != null && hint.totalSteps != null
            ? ` · STEP ${hint.stepIndex + 1} / ${hint.totalSteps}`
            : ''}`}
        </Text>
      </View>
      {!!hint.label && (
        <Text size="xs" weight="bold" color="primary" style={{ marginTop: 2 }}>
          {hint.label}
        </Text>
      )}
      {!!hint.subtitle && (
        <Text size="xxs" color="secondary" style={{ lineHeight: 15, marginTop: 2 }}>
          {hint.subtitle}
        </Text>
      )}
    </View>
  );
}

export function DeckEmbeddedToolRunner({ toolSlug, colors, recipeHint, lastRunId, onRunStarted }) {
  const formRef = useRef(null);
  const [activeRunId, setActiveRunId] = useState(lastRunId || null);
  const [restoredRun, setRestoredRun] = useState(null);
  const [restoreError, setRestoreError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const startingRef = useRef(false);
  const { addToast } = useToastStore((s) => s.actions);

  const tool = useToolStore((s) => s.toolsBySlug[toolSlug]);
  const fetchToolBySlug = useToolStore((s) => s.fetchToolBySlug);
  const isLoadingDetail = useToolStore((s) => s.detailLoadingBySlug[toolSlug]);
  const detailError = useToolStore((s) => s.detailErrorBySlug[toolSlug]);
  const runError = useToolStore((s) => s.runErrorByToolSlug[toolSlug]);
  const runTool = useToolStore((s) => s.runTool);
  const runs = useToolStore((s) => s.runsByToolSlug[toolSlug] ?? EMPTY_TOOL_RUNS);
  const fetchRuns = useToolStore((s) => s.fetchRuns);

  useEffect(() => {
    if (!toolSlug) return;
    setActiveRunId(lastRunId || null);
    setRestoredRun(null);
    setRestoreError(null);
    fetchToolBySlug(toolSlug).catch(() => {});
    fetchRuns({ toolSlug, limit: 12 }).catch(() => {});
  }, [toolSlug, lastRunId, fetchToolBySlug, fetchRuns]);

  const activeRun = useMemo(
    () => runs.find((r) => (r.id || r._id) === activeRunId) || (restoredRun?.id === activeRunId ? restoredRun : null),
    [runs, activeRunId, restoredRun],
  );
  const isRunning = isStarting || ['pending', 'running', 'queued', 'processing'].includes(activeRun?.status);

  // The saved result may be older than the tool's recent-history page.
  useEffect(() => {
    if (!activeRunId || activeRun) return;
    let cancelled = false;
    toolRunsApi.getById(activeRunId).then(data => {
      if (!cancelled) setRestoredRun(data.toolRun || null);
    }).catch(err => {
      if (!cancelled) setRestoreError(err?.message || 'Could not load the saved result.');
    });
    return () => { cancelled = true; };
  }, [activeRunId, activeRun]);

  const palette = useMemo(() => ({ fg: colors.text_color_secondary, bg: colors.solid_color_secondary }), [colors]);
  const activeStatus = useMemo(
    () => (activeRun ? getRunStatusVisual(activeRun.status, colors, palette) : null),
    [activeRun, colors, palette],
  );

  const handleSubmit = useCallback(
    async (inputs, opts = {}) => {
      if (!toolSlug || startingRef.current) return;
      startingRef.current = true;
      setIsStarting(true);
      setActiveRunId(null);
      setRestoreError(null);
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        Keyboard.dismiss();
      }
      try {
        const newRun = await runTool(toolSlug, {
          inputs,
          modelId: opts.modelId || null,
          modelIds: Array.isArray(opts.modelIds) && opts.modelIds.length ? opts.modelIds : null,
          operation: opts.operation || null,
        });
        const rid = newRun?.id || newRun?._id;
        if (rid) {
          setActiveRunId(rid);
          setRestoredRun(newRun);
          onRunStarted?.(rid);
        }
        if (newRun?.status === 'failed') {
          addToast(newRun?.error?.message || 'Tool run failed', 'error');
        }
      } catch (e) {
        addToast(e?.message || 'Tool run failed', 'error');
      } finally {
        startingRef.current = false;
        setIsStarting(false);
      }
    },
    [toolSlug, runTool, addToast, onRunStarted],
  );

  if (!toolSlug) return null;

  if (isLoadingDetail && !tool?.fields) {
    return (
      <View style={styles.toolCenter}>
        <ActivityIndicator color={colors.text_color_secondary} />
      </View>
    );
  }

  if (detailError && !tool?.fields) {
    return (
      <View style={styles.toolCenter}>
        <Text size="xs" color="secondary" style={{ textAlign: 'center', paddingHorizontal: 16 }}>
          {detailError}
        </Text>
      </View>
    );
  }

  if (!tool?.fields) {
    return (
      <View style={styles.toolCenter}>
        <Text size="xs" color="secondary">
          Tool not found.
        </Text>
      </View>
    );
  }

  const showResultBlock = !!activeRunId || !!isRunning;

  return (
    <ScrollView
      style={[styles.toolScroll, { backgroundColor: colors.solid_color_primary }]}
      contentContainerStyle={styles.toolScrollContent}
      keyboardShouldPersistTaps="handled"
    >
      <RecipeHintBanner hint={recipeHint} colors={colors} />
      <View style={[styles.toolCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
        <Text size="xxs" weight="bold" color="secondary" style={styles.toolSectionLabel}>
          Configure
        </Text>
        <ToolForm
          ref={formRef}
          tool={tool}
          isRunning={!!isRunning}
          error={runError}
          onSubmit={handleSubmit}
          hideExamples
        />
      </View>

      <View style={[styles.toolCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
        <View style={styles.toolResultHeader}>
          <Icon name={OUTPUT_TYPE_ICON[tool?.outputType] || 'sparkles'} size={12} color={palette.fg} />
          <Text size="xxs" weight="bold" color="secondary">
            Result
          </Text>
          {showResultBlock && activeStatus && (
            <View style={styles.toolStatusRow}>
              <Icon name={activeStatus.icon} size={9} color={activeStatus.color} solid />
              <Text size="xxs" weight="bold" color="secondary">
                {activeStatus.label}
              </Text>
            </View>
          )}
        </View>
        {showResultBlock ? (
          <View style={styles.toolResultStack}>
            {restoreError && <Text size="xs" color="secondary">{restoreError}</Text>}
            {activeRun && <ToolProgressIndicator run={activeRun} onUpdate={setRestoredRun} />}
            {isRunning && !activeRun && (
              <View style={styles.runStartingRow}>
                <ActivityIndicator size="small" color={colors.text_color_secondary} />
                <Text size="xs" color="secondary" weight="medium">
                  Running…
                </Text>
              </View>
            )}
            {activeRun && <ToolResultDisplay run={activeRun} />}
          </View>
        ) : (
          <ToolEmptyResult tool={tool} palette={palette} fillColumn={false} />
        )}
      </View>
    </ScrollView>
  );
}

const DeckRefColumn = React.memo(
  ({
    column,
    deckSlug,
    columnIndex,
    totalColumns,
    isFocused = false,
    onDragStart,
    setRef,
    isBeingDragged = false,
    isAnimatingIn = false,
    isRemoving = false,
  }) => {
    const { store: useDeckStore } = useDeckBinding();
    const { colors } = useTheme();
    const { width, type } = column;
    const meta = TYPE_META[type] || TYPE_META.tool;

    const { removeColumn, setFocusedColumnId, setColumnConversation, updateColumn } = useDeckStore((s) => s.actions);

    const [pickerQuery, setPickerQuery] = useState('');
    const [agentRows, setAgentRows] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [agentsError, setAgentsError] = useState(null);
    const [toolRows, setToolRows] = useState([]);
    const [toolsLoading, setToolsLoading] = useState(false);
    const [toolsError, setToolsError] = useState(null);

    const allModelIds = useModelStore((s) => s.allModelIds);
    const modelsMap = useModelStore((s) => s.models);
    const fetchAllModels = useModelStore((s) => s.fetchAllModels);
    const addModels = useModelStore((s) => s.addModels);
    const isLoadingAllModels = useModelStore((s) => s.isLoadingAllModels);
    const [isSearchingModels, setIsSearchingModels] = useState(false);
    const [modelSearchResults, setModelSearchResults] = useState([]);
    const [modelSearchError, setModelSearchError] = useState(null);

    const addAgents = useAgentStore((s) => s.addAgents);

    const handleContainerRef = useCallback(
      (node) => {
        setRef?.(column.id, node);
      },
      [setRef, column.id],
    );

    const handleDragStart = useCallback(
      (e) => {
        onDragStart?.(column.id, e);
      },
      [onDragStart, column.id],
    );

    const handleRemove = useCallback(() => {
      removeColumn(column.id, deckSlug);
    }, [column.id, deckSlug, removeColumn]);

    const handleFocus = useCallback(() => {
      setFocusedColumnId(column.id);
    }, [column.id, setFocusedColumnId]);

    const containerStyle = useMemo(
      () => [
        deckPaneStyles.pane,
        {
          width: isRemoving ? 0 : width,
          borderColor: isFocused ? colors.border_color_primary : colors.border_color_secondary,
          backgroundColor: colors.solid_color_primary,
          opacity: isAnimatingIn || isRemoving ? 0 : isBeingDragged ? 0.5 : 1,
        },
      ],
      [width, colors, isFocused, isBeingDragged, isAnimatingIn, isRemoving],
    );

    useEffect(() => {
      if (type !== 'model') return;
      fetchAllModels(1).catch(() => {});
    }, [type, fetchAllModels]);

    useEffect(() => {
      if (type !== 'agent') return;
      const q = pickerQuery.trim();
      let cancelled = false;

      const run = async () => {
        setAgentsLoading(true);
        setAgentsError(null);
        try {
          if (!q) {
            const recent = await agentsApi.getRecentAgents();
            if (cancelled) return;
            addAgents(recent || []);
            setAgentRows(recent || []);
          } else {
            const data = await agentsApi.search(q, 1);
            if (cancelled) return;
            const agents = data?.agents || [];
            addAgents(agents);
            setAgentRows(agents);
          }
        } catch {
          if (!cancelled) {
            setAgentRows([]);
            setAgentsError('Could not load agents. Check your connection and try searching again.');
          }
        } finally {
          if (!cancelled) setAgentsLoading(false);
        }
      };

      const delay = q ? 280 : 0;
      const h = setTimeout(() => {
        void run();
      }, delay);
      return () => {
        cancelled = true;
        clearTimeout(h);
      };
    }, [pickerQuery, type, addAgents]);

    useEffect(() => {
      if (type !== 'tool' || column.config?.toolSlug) return;
      const q = pickerQuery.trim();
      let cancelled = false;
      const delay = q ? 280 : 0;
      const h = setTimeout(async () => {
        setToolsLoading(true);
        setToolsError(null);
        try {
          const data = await toolsApi.list({
            limit: 80,
            page: 1,
            ...(q ? { q } : {}),
          });
          if (cancelled) return;
          const tools = data.tools || [];
          setToolRows(tools);
          useToolStore.getState().upsertTools(tools);
        } catch {
          if (!cancelled) {
            setToolRows([]);
            setToolsError('Could not load tools. Check your connection and try searching again.');
          }
        } finally {
          if (!cancelled) setToolsLoading(false);
        }
      }, delay);
      return () => {
        cancelled = true;
        clearTimeout(h);
      };
    }, [type, pickerQuery, column.config?.toolSlug]);

    useEffect(() => {
      if (type !== 'tool' || column.config?.toolSlug || !column.ref?.id) return;
      const prevConfig = column.config || {};
      let cancelled = false;
      (async () => {
        try {
          const data = await toolsApi.list({ limit: 150, page: 1 });
          if (cancelled) return;
          const tools = data.tools || [];
          const match = tools.find((t) => String(t.id) === String(column.ref.id));
          if (match?.slug) {
            updateColumn(column.id, {
              config: { ...prevConfig, toolSlug: match.slug },
              label: column.label || match.name || '',
            });
          }
        } catch {
          /* non-fatal */
        }
      })();
      return () => {
        cancelled = true;
      };
    }, [type, column.id, column.ref?.id, column.config?.toolSlug, column.label, column.config, updateColumn]);

    useEffect(() => {
      if (type !== 'model') return;
      setModelSearchResults([]);
      setModelSearchError(null);
      if (!pickerQuery.trim()) {
        setIsSearchingModels(false);
        return;
      }
      let cancelled = false;
      setIsSearchingModels(true);
      const t = setTimeout(async () => {
        try {
          const data = await modelsApi.search(pickerQuery.trim(), 1);
          if (cancelled) return;
          addModels(data.models || []);
          setModelSearchResults(data.models || []);
        } catch (err) {
          if (!cancelled) setModelSearchError(err?.message || 'Could not search models.');
        } finally {
          if (!cancelled) setIsSearchingModels(false);
        }
      }, 280);
      return () => { cancelled = true; clearTimeout(t); };
    }, [pickerQuery, type, addModels]);

    const modelListIds = useMemo(() => {
      if (pickerQuery.trim()) return (modelSearchResults || []).map((m) => m.id).filter(Boolean);
      return allModelIds || [];
    }, [pickerQuery, modelSearchResults, allModelIds]);

    const toolTitle = useToolStore((s) => s.toolsBySlug[column.config?.toolSlug || '']?.name);

    // Optional recipe hint stamped onto the column at clone time by
    // jsonRoutes resolveDeckColumnsFromTemplate. Surfaces the recipe's
    // editorial column title + subtitle so the empty deck doesn't feel
    // like a hard cut from the gorgeous recipe page.
    const recipeHint = useMemo(() => {
      const cfg = column.config || {};
      const label = cfg.recipeLabel || '';
      const subtitle = cfg.recipeSubtitle || '';
      if (!label && !subtitle) return null;
      return {
        label,
        subtitle,
        modality: cfg.recipeModality || '',
        stepIndex: columnIndex,
        totalSteps: totalColumns,
      };
    }, [column.config, columnIndex, totalColumns]);

    const openExternalHref = useMemo(() => {
      if (type === 'model' && column.ref?.id) return `/m/${column.ref.id}`;
      if (type === 'agent' && column.ref?.id) return `/g/${column.ref.id}`;
      if (type === 'tool' && column.config?.toolSlug) return `/tools/${column.config.toolSlug}`;
      return null;
    }, [type, column.ref?.id, column.config?.toolSlug]);

    const hasBackedResource = useMemo(() => {
      if (type === 'model' || type === 'agent') return !!column.ref?.id;
      if (type === 'tool') return !!column.config?.toolSlug;
      return false;
    }, [type, column.ref?.id, column.config?.toolSlug]);

    const handleChangeResource = useCallback(() => {
      if (type === 'model') {
        updateColumn(column.id, { type: 'model', ref: { kind: 'Model', id: null }, label: '' });
      } else if (type === 'agent') {
        updateColumn(column.id, { type: 'agent', ref: { kind: 'Agent', id: null }, label: '' });
      } else if (type === 'tool') {
        updateColumn(column.id, {
          type: 'tool',
          ref: { kind: 'Tool', id: null },
          label: '',
          config: {},
        });
      }
    }, [type, column.id, updateColumn]);

    const renderModelBody = () => {
      if (column.ref?.id) {
        return (
          <DeckResourceStartPanel
            kind="model"
            columnId={column.id}
            deckSlug={deckSlug}
            resourceId={String(column.ref.id)}
            recipeHint={recipeHint}
            onConversationStarted={(conversationId) => setColumnConversation(column.id, conversationId, deckSlug)}
            onPickDifferent={() =>
              updateColumn(column.id, {
                type: 'model',
                ref: { kind: 'Model', id: null },
                label: '',
              })
            }
          />
        );
      }

      return (
        <View style={styles.pickerWrap}>
          <View
            style={[
              styles.searchShell,
              { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
            ]}
          >
            <Icon name="magnifying-glass" size={14} color={colors.text_color_tertiary} />
            <TextInput
              value={pickerQuery}
              onChangeText={setPickerQuery}
              placeholder="Search models…"
              placeholderTextColor={colors.text_color_tertiary}
              style={[styles.searchInputFlex, { color: colors.text_color_primary }]}
              autoCorrect={false}
              autoCapitalize="none"
            />
          </View>
          {modelSearchError && <Text size="xs" color="secondary">{modelSearchError}</Text>}
          {!modelSearchError && !!pickerQuery.trim() && !isSearchingModels && !modelListIds.length && <Text size="xs" color="secondary">No models found</Text>}
          {(isLoadingAllModels || isSearchingModels) && !modelListIds.length ? (
            <ActivityIndicator style={{ marginTop: 16 }} color={colors.text_color_tertiary} />
          ) : (
            <ScrollView style={styles.pickerList} keyboardShouldPersistTaps="handled">
              {modelListIds.map((mid) => {
                const m = modelsMap[mid];
                if (!m) return null;
                return (
                  <Pressable
                    key={mid}
                    onPress={() =>
                      updateColumn(column.id, {
                        type: 'model',
                        ref: { kind: 'Model', id: mid },
                        label: m.name || '',
                      })
                    }
                    style={({ hovered }) => [
                      styles.pickerRow,
                      { borderBottomColor: colors.border_color_secondary },
                      Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
                    ]}
                  >
                    <View style={styles.pickerRowInner}>
                      <View
                        style={[
                          styles.pickerLead,
                          { backgroundColor: m.logoPath ? '#fff' : colors.solid_color_tertiary },
                        ]}
                      >
                        {m.logoPath ? (
                          <Image
                            source={{ uri: m.logoPath }}
                            style={styles.pickerLeadImage}
                            contentFit="cover"
                          />
                        ) : (
                          <Icon name="cube" size={14} color={colors.text_color_secondary} />
                        )}
                      </View>
                      <Text size="xs" weight="semibold" color="primary" numberOfLines={1} style={styles.pickerTitle}>
                        {m.name}
                      </Text>
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>
          )}
        </View>
      );
    };

    const renderAgentBody = () => {
      if (column.ref?.id) {
        return (
          <DeckResourceStartPanel
            kind="agent"
            columnId={column.id}
            deckSlug={deckSlug}
            resourceId={String(column.ref.id)}
            recipeHint={recipeHint}
            onConversationStarted={(conversationId) => setColumnConversation(column.id, conversationId, deckSlug)}
            onPickDifferent={() =>
              updateColumn(column.id, {
                type: 'agent',
                ref: { kind: 'Agent', id: null },
                label: '',
              })
            }
          />
        );
      }

      return (
        <View style={styles.pickerWrap}>
          <View
            style={[
              styles.searchShell,
              { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
            ]}
          >
            <Icon name="magnifying-glass" size={14} color={colors.text_color_tertiary} />
            <TextInput
              value={pickerQuery}
              onChangeText={setPickerQuery}
              placeholder="Search agents…"
              placeholderTextColor={colors.text_color_tertiary}
              style={[styles.searchInputFlex, { color: colors.text_color_primary }]}
              autoCorrect={false}
              autoCapitalize="none"
            />
          </View>
          {agentsLoading && !agentRows.length ? (
            <ActivityIndicator style={{ marginTop: 16 }} color={colors.text_color_tertiary} />
          ) : (
            <ScrollView style={styles.pickerList} keyboardShouldPersistTaps="handled">
              {!agentRows.length && (
                <Text size="xs" color="secondary" style={{ padding: 16 }}>
                  {agentsError || (pickerQuery.trim() ? 'No agents found.' : 'No recent agents. Search for an agent.')}
                </Text>
              )}
              {agentRows.map((a) => (
                <Pressable
                  key={a.id}
                  onPress={() =>
                    updateColumn(column.id, {
                      type: 'agent',
                      ref: { kind: 'Agent', id: a.id },
                      label: a.name || '',
                    })
                  }
                  style={({ hovered }) => [
                    styles.pickerRow,
                    { borderBottomColor: colors.border_color_secondary },
                    Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
                  ]}
                >
                  <View style={styles.pickerRowInner}>
                    <View style={[styles.pickerLead, { backgroundColor: colors.solid_color_tertiary }]}>
                      {a.avatarPath ? (
                        <Image
                          source={{ uri: a.avatarPath }}
                          style={styles.pickerLeadImage}
                          contentFit="cover"
                        />
                      ) : (
                        <Icon name="user-astronaut" size={14} color={colors.text_color_secondary} />
                      )}
                    </View>
                    <Text size="xs" weight="semibold" color="primary" numberOfLines={1} style={styles.pickerTitle}>
                      {a.name}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>
      );
    };

    const renderToolBody = () => {
      const slug = column.config?.toolSlug;
      if (!slug) {
        return (
          <View style={styles.pickerWrap}>
            <View
              style={[
                styles.searchShell,
                { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
              ]}
            >
              <Icon name="magnifying-glass" size={14} color={colors.text_color_tertiary} />
              <TextInput
                value={pickerQuery}
                onChangeText={setPickerQuery}
                placeholder="Search tools…"
                placeholderTextColor={colors.text_color_tertiary}
                style={[styles.searchInputFlex, { color: colors.text_color_primary }]}
                autoCorrect={false}
                autoCapitalize="none"
              />
            </View>
            {toolsLoading && !toolRows.length ? (
              <ActivityIndicator style={{ marginTop: 24 }} color={colors.text_color_tertiary} />
            ) : (
              <ScrollView style={styles.pickerList} keyboardShouldPersistTaps="handled">
                {!toolRows.length && (
                  <Text size="xs" color="secondary" style={{ padding: 16 }}>
                    {toolsError || 'No tools found.'}
                  </Text>
                )}
                {toolRows.map((t) => {
                  const iconName = t.icon || OUTPUT_TYPE_ICON[t.outputType] || 'wand-magic-sparkles';
                  return (
                    <Pressable
                      key={t.id || t.slug}
                      onPress={() =>
                        updateColumn(column.id, {
                          type: 'tool',
                          ref: { kind: 'Tool', id: t.id || null },
                          label: t.name || '',
                          config: { ...column.config, toolSlug: t.slug },
                        })
                      }
                      style={({ hovered }) => [
                        styles.pickerRow,
                        { borderBottomColor: colors.border_color_secondary },
                        Platform.OS === 'web' && hovered ? { backgroundColor: colors.solid_color_tertiary } : null,
                      ]}
                    >
                      <View style={styles.pickerRowInner}>
                        <View style={[styles.pickerLead, { backgroundColor: colors.solid_color_secondary }]}>
                          <Icon name={iconName} size={15} color={colors.text_color_secondary} />
                        </View>
                        <Text size="xs" weight="semibold" color="primary" numberOfLines={1} style={styles.pickerTitle}>
                          {t.name}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </ScrollView>
            )}
          </View>
        );
      }

      return <DeckEmbeddedToolRunner
        toolSlug={slug}
        colors={colors}
        recipeHint={recipeHint}
        lastRunId={column.config?.lastToolRunId}
        onRunStarted={(runId) => {
          const ownerDeck = useDeckStore.getState().decks.find(d => d.slug === deckSlug);
          const freshColumn = ownerDeck?.columns.find(c => c.id === column.id);
          // Do not overwrite a resource changed while this run was starting.
          if (freshColumn?.config?.toolSlug !== slug) return;
          updateColumn(column.id, { config: { ...freshColumn.config, lastToolRunId: runId } }, deckSlug);
        }}
      />;
    };

    const headerTitle = column.label || (type === 'tool' && toolTitle ? toolTitle : null) || meta.label;

    return (
      <View ref={handleContainerRef} style={containerStyle} onPointerDown={handleFocus}>
        <DeckColumnHeader
          chat={null}
          columnId={column.id}
          columnIndex={columnIndex}
          totalColumns={totalColumns}
          onRemove={handleRemove}
          onDragStart={handleDragStart}
          isFocused={isFocused}
          titleOverride={headerTitle}
          resourceKind={type === 'model' || type === 'agent' || type === 'tool' ? type : undefined}
          openExternalHref={openExternalHref || undefined}
          onChangeResource={hasBackedResource ? handleChangeResource : undefined}
        />

        <View style={styles.body}>
          {type === 'model' && renderModelBody()}
          {type === 'agent' && renderAgentBody()}
          {type === 'tool' && renderToolBody()}
        </View>
      </View>
    );
  },
);

DeckRefColumn.displayName = 'DeckRefColumn';

export default DeckRefColumn;

const styles = StyleSheet.create({
  body: {
    flex: 1,
    minHeight: 0,
  },
  centerStack: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    gap: 12,
  },
  pickerWrap: {
    flex: 1,
    padding: 20,
    gap: 16,
    minHeight: 0,
  },
  searchShell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchInputFlex: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    paddingVertical: 4,
    ...(Platform.OS === 'web' && { outlineStyle: 'none' }),
  },
  pickerList: {
    flex: 1,
    minHeight: 0,
  },
  pickerRow: {
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  pickerRowInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
    minWidth: 0,
  },
  pickerLead: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  pickerLeadImage: {
    width: 32,
    height: 32,
  },
  pickerTitle: {
    flex: 1,
    minWidth: 0,
  },
  retryBtn: {
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  toolScroll: {
    flex: 1,
  },
  toolScrollContent: {
    padding: 20,
    gap: 20,
  },
  toolCard: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 16,
    gap: 12,
  },
  recipeHintBanner: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 10,
    gap: 2,
  },
  recipeHintBannerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  recipeHintBannerEyebrow: {
    fontSize: 12,
  },
  toolSectionLabel: {
    marginBottom: 2,
  },
  toolResultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  toolStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginLeft: 'auto',
  },
  toolResultStack: {
    gap: 8,
    minHeight: 40,
  },
  runStartingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
  },
  toolCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
});
